import { CButton, CCard, CCol, CRow } from '@coreui/react';
import dayjs from 'dayjs';
import throttle from 'lodash/throttle';
import React, { useCallback, useRef, useState, useEffect } from 'react';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { useOutsideHandling } from '../../utils';
import { DATE_RANGE_PRESETS } from '../../constants';
import PropTypes from 'prop-types';

const DatePickerBox = ({ toggleDateBox, today, dateRange, setDateRange, setDateHistory, setDateHistoryEnd, customClasses }) => {
    const maxDate = today['$d'];
    const presetsToDisplay = [
        [DATE_RANGE_PRESETS.LAST_7_DAYS, DATE_RANGE_PRESETS.LAST_WEEK],
        [DATE_RANGE_PRESETS.LAST_14_DAYS, DATE_RANGE_PRESETS.THIS_MONTH],
        [DATE_RANGE_PRESETS.LAST_30_DAYS, DATE_RANGE_PRESETS.LAST_MONTH],
    ];

    const [dateRangeConfig, setDateRangeConfig] = useState({
        datePreset: null,
    });

    const initialDateRange = {
        selection: {
            startDate: dateRange.selection.startDate['$d'],
            endDate: dateRange.selection.endDate['$d'],
            key: 'selection',
        },
   };

    const customDateWrapperRef = useRef(null);
    const [focusedRange, setFocusedRange] = useState([0, 0]);
    const [calendarValue, setCalendarValue] = useState(initialDateRange);

    const ranges = Object.values(calendarValue).map((val) => val);

    useOutsideHandling(customDateWrapperRef, toggleDateBox, 'mouseup');

    const onApplyButtonClicked = () => {
        const { selection } = calendarValue;

        const newDateRange = {
            selection: {
                startDate: dayjs(selection.startDate),
                endDate: selection.endDate ? dayjs(selection.endDate) : dayjs(selection.startDate),
            },
        };

        if (setDateHistory && setDateHistory) {
            setDateHistory(newDateRange.selection.startDate);
            setDateHistoryEnd(newDateRange.selection.endDate);
        }

        setDateRange(newDateRange);
        toggleDateBox();
    };

    const onRangeFocusChange = (range) => {
        setFocusedRange(range);
   };

    const onChangeDateRange = (ranges) => {
        const { selection } = ranges;
        let newRanges = ranges;

        if (selection) {
            if (selection.startDate > maxDate) {
                newRanges = {
                    selection: {
                        ...selection,
                        startDate: maxDate,
                    },
                };
            } else if (selection.endDate > maxDate) {
                newRanges = {
                    selection: {
                        ...selection,
                        endDate: maxDate,
                    },
                };
            }
        }

        setCalendarValue({ ...calendarValue, ...newRanges });
   };

   const onClickDatePreset = (preset) => {
        let dateRange;

        switch (preset) {
            case DATE_RANGE_PRESETS.TODAY:
                dateRange = {
                    startDate: today['$d'],
                    endDate: today['$d'],
                };
                break;
            case DATE_RANGE_PRESETS.LAST_7_DAYS:
                dateRange = {
                    startDate: today.subtract(7, 'day')['$d'],
                    endDate: today.subtract(1, 'day')['$d'],
                };
                break;
            case DATE_RANGE_PRESETS.LAST_14_DAYS:
                dateRange = {
                    startDate: today.subtract(14, 'day')['$d'],
                    endDate: today.subtract(1, 'day')['$d'],
                };
                break;
            case DATE_RANGE_PRESETS.LAST_30_DAYS:
                dateRange = {
                    startDate: today.subtract(30, 'day')['$d'],
                    endDate: today.subtract(1, 'day')['$d'],
                };
                break;
            case DATE_RANGE_PRESETS.LAST_WEEK:
                const lastWeek = today.subtract(1, 'week');
                dateRange = {
                    startDate: lastWeek.startOf('week')['$d'],
                    endDate: lastWeek.endOf('week')['$d'],
                };
                break;
            case DATE_RANGE_PRESETS.THIS_MONTH:
                dateRange = {
                    startDate: today.startOf('month')['$d'],
                    endDate: today.endOf('month')['$d'],
                };
                break;
            case DATE_RANGE_PRESETS.LAST_MONTH:
                const lastMonth = today.subtract(1, 'month');
                dateRange = {
                    startDate: lastMonth.startOf('month')['$d'],
                    endDate: lastMonth.endOf('month')['$d'],
                };
                break;
            default:
                dateRange = calendarValue.selection;
        }

        setDateRangeConfig({ datePreset: preset });
        setCalendarValue({
            ...calendarValue,
            selection: {
                ...dateRange,
                key: 'selection',
            },
        });
    };

    return (
        <CCard className={`custom-date-wrapper ${customClasses}`} ref={customDateWrapperRef}>
               <DateRangePicker
                    months={1}
                    maxDate={maxDate}
                    direction='vertical'
                    color='#3c4b64'
                    rangeColors={['#E4E5FF', '#FFF5D6']}
                    scroll={{ enabled: true }}
                    ranges={ranges}
                    staticRanges={[]}
                    inputRanges={[]}
                    weekdayDisplayFormat='EEEEEE'
                    monthDisplayFormat='MMMM'
                    editableDateInputs={true}
                    showMonthAndYearPickers={true}
                    retainEndDateOnFirstSelection={true}
                    startDatePlaceholder='Start Date'
                    endDatePlaceholder='End Date'
                    onChange={onChangeDateRange}
                    focusedRange={focusedRange}
                    onRangeFocusChange={onRangeFocusChange}
               />

                <div className='presets-wrapper'>
                    <div className='easy-presets'>
                        <p className='label'>Easy presets</p>

                        <CButton
                            className={`${dateRangeConfig.datePreset === DATE_RANGE_PRESETS.TODAY ? 'active' : ''}`}
                            onClick={() => onClickDatePreset(DATE_RANGE_PRESETS.TODAY)}
                        >
                            Go to Today
                        </CButton>
                    </div>

                    <div className='presets'>
                        {presetsToDisplay.map((presets, i) => {
                            return (
                                <CRow key={i}>
                                    {presets.map((preset, presetIndex) => (
                                        <CCol key={presetIndex} sm={6} className='preset-col'>
                                            <CButton className={`${preset === dateRangeConfig.datePreset ? 'active' : ''}`} onClick={() => onClickDatePreset(preset)}>
                                                {preset}
                                            </CButton>
                                        </CCol>
                                    ))}
                                </CRow>
                            );
                        })}
                    </div>
                </div>

                <div className='bottom-buttons'>
                    <CButton onClick={toggleDateBox}>Cancel</CButton>

                    <CButton color='primary' onClick={onApplyButtonClicked}>
                        Apply
                    </CButton>
                </div>
          </CCard>
    )
}

DatePickerBox.propTypes = {
    toggleDateBox: PropTypes.func,
    today: PropTypes.any,
    dateRange: PropTypes.any,
    setDateRange: PropTypes.func,
    setDateHistory: PropTypes.func,
    setDateHistoryEnd: PropTypes.func,
    customClasses: PropTypes.string,
};

const CustomDatePickerRange = ({ isForm, startDate, endDate, today, setDateHistory, setDateHistoryEnd, customClasses }) => {
    const dateFormat = 'MMM D, YYYY';

    const [dateBoxOpened, setDateBoxOpened] = useState(false);
    const [dateRange, setDateRange] = useState({
        selection: {
            startDate,
            endDate,
        },
    });

    useEffect(() => {
        setDateRange({
            selection: {
                startDate,
                endDate,
            },
        });
    }, [startDate, endDate]);

    const toggleDateBox = useCallback( // eslint-disable-line
        throttle(() => setDateBoxOpened((s) => !s),
            100,
            {
                trailing: false
            }
        ),
        []
    );

    const value = `${dateRange.selection.startDate.format(dateFormat)} - ${dateRange.selection.endDate.format(dateFormat)}`;

    return (
        <div className="react-datepicker-wrapper custom-date-picker">
            <div className="react-datepicker__input-container" onClick={toggleDateBox}>
                {isForm ? <input className={`form-control bg-white`} type="text" value={value} readOnly /> : value }
            </div>

            {dateBoxOpened && <DatePickerBox
                today={today}
                dateRange={dateRange}
                setDateRange={setDateRange}
                setDateHistory={setDateHistory}
                setDateHistoryEnd={setDateHistoryEnd}
                toggleDateBox={toggleDateBox}
                customClasses={customClasses}
            />}
        </div>
    );
};

CustomDatePickerRange.propTypes = {
    isForm: PropTypes.bool,
    startDate: PropTypes.any,
    endDate: PropTypes.any,
    today: PropTypes.any,
    setDateHistory: PropTypes.func,
    setDateHistoryEnd: PropTypes.func,
    customClasses: PropTypes.string,
};

export default CustomDatePickerRange;
